import { InformalFeedbackRequest, InformalFeedbackRequestState } from 'api/clients/InformalFeedbackRequestsClient'
import { Heading } from 'blaze-ui/Heading/Heading'
import { Drawer } from 'components/Drawer'
import { DrawerProps } from 'components/Drawer/Root'
import { observer } from 'mobx-react-lite'
import Styles from './ReviewSummaryModal.module.scss'
import { Text } from 'blaze-ui/Text/Text'
import { DueDatePicker } from 'components/DueDatePicker/DueDatePicker'
import { useRootStore } from 'stores/RootStore'
import { useQueryClient } from 'react-query'
import { useStore } from 'react-redux'
import { useAuthOrUnregisteredUser } from 'hooks/useAuthOrUnregisteredUser'
import { useCurrentArticleMeta } from 'hooks/useCurrentArticleMeta'
import { InformalFeedbackRequestsService } from 'api/services/AlmanacFiles/InformalFeedbackRequestsService'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { showErrorMessageToast } from 'common/MessageToasts/ErrorMessageToast/ErrorMessageToast'
import { Permission } from 'types'
import Calendar2 from 'blaze-ui/icons/20/Calendar2'
import dayjs from 'dayjs'
import { RequesterCard } from './RequesterCard/RequesterCard'
import { ReviewerCard } from './ReviewersCard/ReviewerCard'
import { EndReviewDrawer } from './EndReviewModal/EndReviewDrawer'

interface Props {
  request: InformalFeedbackRequest
}

export const ReviewSummaryDrawer = observer(({ request, ...props }: DrawerProps & Props) => {
  const queryClient = useQueryClient()
  const { designStore, drawersStore } = useRootStore()
  const { isRegistered } = useAuthOrUnregisteredUser()
  const store = useStore()
  const article = useCurrentArticleMeta()

  const handleEndsOnChange = async (value: string | null) => {
    if (!value) {
      return
    }
    const res = await InformalFeedbackRequestsService.update(
      store,
      request.id,
      request.almanac_file.id,
      { endsOn: value },
      queryClient,
    )
    if (res?.type === SUCCESS_RESPONSE_TYPE) {
      showToastMessage('Due date updated successfully')
    } else {
      showErrorMessageToast('Something went wrong, try again')
    }
  }

  const hasEditAccess = designStore?.file?.permissions.can_edit || article?.current_user_access === Permission.Edit
  const canEditRequest = hasEditAccess && request.state === InformalFeedbackRequestState.Open
  const reviewerFieldWidth = Math.max(...request.reviews.map((r) => r.reviewer.display_name?.length || 0))

  return (
    <Drawer.Root {...props}>
      <Drawer.Portal>
        <Drawer.Content fullHeight>
          <Drawer.InnerContent isSnappedTop>
            <Drawer.Top>
              <Drawer.Close />
              <Drawer.Title asChild>
                <Heading level={5}>Review Summary</Heading>
              </Drawer.Title>
              <Drawer.Action position="right" onPress={() => drawersStore.openDrawer(EndReviewDrawer, { request })}>
                End Review
              </Drawer.Action>
            </Drawer.Top>
            <Drawer.Container>
              <div className={Styles.mobileBody}>
                <div className={Styles.mobileCardContainer}>
                  <div className={Styles.mobileHeader}>
                    <Text variant="smallList" color="var(--dark=80)">
                      Requester
                    </Text>

                    {canEditRequest && (
                      <DueDatePicker
                        value={request.ends_on}
                        onChange={handleEndsOnChange}
                        minValue={dayjs().format('YYYY-MM-DD')}
                        shape="squared"
                        variant="secondary"
                        size="xs"
                        readOnly={!isRegistered}
                        frontIcon={Calendar2}
                        showClearDate={false}
                      />
                    )}
                  </div>
                  <RequesterCard request={request} />
                </div>
                <div className={Styles.mobileCardContainer}>
                  <div className={Styles.mobileHeader}>
                    <Text variant="smallList" color="var(--dark=80)">
                      Invitees
                    </Text>
                  </div>

                  <div className={Styles.reviewers}>
                    {request.reviews.map((review) => (
                      <ReviewerCard
                        key={review.id}
                        review={review}
                        request={request}
                        reviewerFieldWidth={reviewerFieldWidth * 8}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </Drawer.Container>
          </Drawer.InnerContent>
        </Drawer.Content>
        <Drawer.Overlay />
      </Drawer.Portal>
    </Drawer.Root>
  )
})
